/**
 * AvatarEditDialog Component
 * Modal dialog for editing an existing avatar's metadata.
 * Does NOT replace the file — upload a new avatar for that.
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Save, X } from 'lucide-react';
import type { AgentAvatar, AvatarCategory, UpdateAvatarPayload } from '../types';
import { parseTags } from '../types';

// ============================================
// Tag input (reused from UploadDialog pattern)
// ============================================

interface TagInputProps {
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      const tag = inputValue.trim().replace(',', '');
      if (tag && !tags.includes(tag)) onAdd(tag);
      setInputValue('');
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      onRemove(tags[tags.length - 1]);
    }
  };

  return (
    <Box
      className="min-h-[44px] rounded border px-3 py-2"
      sx={{
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {tags.map((tag) => (
        <Chip key={tag} label={tag} size="small" onDelete={() => onRemove(tag)} sx={{ height: 22, fontSize: '0.7rem' }} />
      ))}
      <input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? 'Type tag + Enter' : ''}
        style={{ border: 'none', outline: 'none', background: 'transparent', minWidth: 80, flex: 1 }}
        className="text-sm dark:text-slate-200"
      />
    </Box>
  );
};

// ============================================
// Props
// ============================================

interface AvatarEditDialogProps {
  open: boolean;
  avatar: AgentAvatar | null;
  onClose: () => void;
  onSubmit: (payload: UpdateAvatarPayload) => Promise<void>;
  isLoading: boolean;
}

// ============================================
// Main Component
// ============================================

export const AvatarEditDialog: React.FC<AvatarEditDialogProps> = ({
  open,
  avatar,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<AvatarCategory | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [nameError, setNameError] = useState('');

  // Populate form when avatar changes
  useEffect(() => {
    if (avatar) {
      setName(avatar.name);
      setCategory((avatar.category as AvatarCategory | '') ?? '');
      setTags(parseTags(avatar.tags));
      setIsDefault(avatar.isDefault);
      setPreviewUrl(avatar.previewUrl ?? '');
      setNameError('');
    }
  }, [avatar]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError('Avatar name is required');
      return;
    }
    if (!avatar) return;

    await onSubmit({
      publicId: avatar.publicId,
      name: name.trim(),
      category: category || undefined,
      tags: tags.length > 0 ? JSON.stringify(tags) : undefined,
      isDefault,
      previewUrl: previewUrl.trim() || undefined,
    });
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  if (!avatar) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle className="flex items-center justify-between text-gray-900 dark:text-slate-100">
        <Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Edit Avatar
          </Typography>
          <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
            {avatar.file.fileName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={isLoading}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box className="flex flex-col gap-4 pt-1">

          {/* Thumbnail preview */}
          {avatar.previewUrl && (
            <Box className="overflow-hidden rounded-lg">
              {avatar.type === 'video' ? (
                <video
                  src={avatar.previewUrl}
                  className="max-h-32 w-full object-contain"
                  style={{ background: '#0f172a' }}
                  muted
                />
              ) : (
                <img
                  src={avatar.previewUrl}
                  alt={avatar.name}
                  className="max-h-32 w-full object-contain"
                  style={{ background: '#0f172a' }}
                />
              )}
            </Box>
          )}

          {/* Name */}
          <TextField
            label="Avatar Name *"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError('');
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            InputProps={{
              sx: { color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined) },
            }}
            InputLabelProps={{
              sx: { color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) },
            }}
          />

          {/* Category */}
          <FormControl fullWidth size="small">
            <InputLabel
              sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) }}
            >
              Category
            </InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value as AvatarCategory | '')}
              sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined) }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="robot">Robot</MenuItem>
              <MenuItem value="anime">Anime</MenuItem>
              <MenuItem value="human">Human</MenuItem>
              <MenuItem value="abstract">Abstract</MenuItem>
            </Select>
          </FormControl>

          {/* Tags */}
          <Box>
            <Typography variant="caption" className="mb-1 block text-gray-500 dark:text-slate-400">
              Tags (press Enter or comma to add)
            </Typography>
            <TagInput
              tags={tags}
              onAdd={(t) => setTags((prev) => [...prev, t])}
              onRemove={(t) => setTags((prev) => prev.filter((x) => x !== t))}
            />
          </Box>

          {/* Override preview URL */}
          <TextField
            label="Preview URL (optional override)"
            fullWidth
            size="small"
            value={previewUrl}
            onChange={(e) => setPreviewUrl(e.target.value)}
            placeholder="https://…"
            InputProps={{
              sx: { color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined) },
            }}
            InputLabelProps={{
              sx: { color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) },
            }}
          />

          {/* Is Default */}
          <FormControlLabel
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3B82F6' },
                }}
              />
            }
            label={
              <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
                Set as default avatar
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          sx={{ textTransform: 'none', borderColor: 'rgba(107,114,128,0.4)', color: '#6B7280' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
          sx={{
            textTransform: 'none',
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
            fontWeight: 600,
          }}
        >
          {isLoading ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
